import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Icon from '../icon/Icon'
import Text from '../Text'

// Utils
import { useAppState } from '../../context/AppContext'

// Types
import { ICompanyMember, ICompanyTerm } from '../../common/models/Company'

// Sub-views
import InsightsMatrixHeatmapView from './InsightsMatrixHeatmapView'
import InsightsMatrixScatterView from './InsightsMatrixScatterView'

export interface IBandConfig {
  cuts: [number, number]
}

export interface IMemberPlot {
  memberId: string
  x: number
  y: number
  // Whether the member was actually assessed on each axis. When false, the
  // x/y still carries the 50 midpoint for positioning, but the axis should be
  // rendered as "not assessed" rather than a real score.
  cultureAssessed: boolean
  skillAssessed: boolean
}

export interface IZoneCount {
  zoneX: 0 | 1 | 2
  zoneY: 0 | 1 | 2
  memberIds: string[]
}

export const BAND_CUTS: IBandConfig = { cuts: [33, 66] }

export const scoreToZone = (score: number): 0 | 1 | 2 => {
  if (score < BAND_CUTS.cuts[0]) return 0
  if (score < BAND_CUTS.cuts[1]) return 1
  return 2
}

const ZONE_TO_BAND_LABEL = ['Low', 'Mid', 'High'] as const

export const personaKeyForScores = (
  skillScore: number,
  cultureScore: number
): string => {
  const skillBand = ZONE_TO_BAND_LABEL[scoreToZone(skillScore)]
  const cultureBand = ZONE_TO_BAND_LABEL[scoreToZone(cultureScore)]
  return `insights.nineGrid.persona${skillBand}Skill${cultureBand}Culture`
}

export const personaKeyForZone = (zoneY: 0 | 1 | 2, zoneX: 0 | 1 | 2): string =>
  `insights.nineGrid.persona${ZONE_TO_BAND_LABEL[zoneY]}Skill${ZONE_TO_BAND_LABEL[zoneX]}Culture`

export type ViewMode = 'heatmap' | 'scatter'

interface IProps {
  zoneCounts: IZoneCount[]
  zoneCountsPrev?: IZoneCount[]
  memberPlots: IMemberPlot[]
  memberPlotsPrev?: IMemberPlot[]
  members: ICompanyMember[]
  selectedTerm?: ICompanyTerm
  compareTo?: ICompanyTerm
  // Controlled from the parent so the simplified/detailed toggle can live
  // outside the grid, in the analytics tab bar.
  view: ViewMode
  setMemberForModal?: (member: ICompanyMember) => void
}

const InsightsMatrix: React.FC<IProps> = ({
  zoneCounts,
  zoneCountsPrev,
  memberPlots,
  memberPlotsPrev,
  members,
  selectedTerm,
  compareTo,
  view,
  setMemberForModal,
}) => {
  const { t } = useTranslation()
  const { theme } = useAppState()

  const [selectedZoneKey, setSelectedZoneKey] = useState<string>('')
  const [hoveredMemberId, setHoveredMemberId] = useState<string>()
  const [pinnedMemberId, setPinnedMemberId] = useState<string>()
  const [selectedClusterId, setSelectedClusterId] = useState<string>()

  const memberById = useMemo(() => {
    const map = new Map<string, ICompanyMember>()
    for (const m of members) map.set(m._id, m)
    return map
  }, [members])

  const plotByMemberId = useMemo(() => {
    const map = new Map<string, IMemberPlot>()
    for (const p of memberPlots) map.set(p.memberId, p)
    return map
  }, [memberPlots])

  const plotByMemberIdPrev = useMemo(() => {
    const map = new Map<string, IMemberPlot>()
    for (const p of memberPlotsPrev ?? []) map.set(p.memberId, p)
    return map
  }, [memberPlotsPrev])

  return (
    <Container>
      <PlotFrame>
        <TopRightIcon type="Thumb" color={theme.colors.textLight} size={20} />
        <BottomLeftIcon
          type="Thumb"
          direction="down"
          color={theme.colors.textLight}
          size={20}
        />
        <SkillAxisLine />
        <SkillAxisLabel textStyle="labelSmallCaps" color="text72">
          <Icon type="Skills" color={theme.colors.textLight} size={16} />
          <span>{t('general.skills')}</span>
        </SkillAxisLabel>
        <CultureAxisLine />
        <CultureAxisLabel textStyle="labelSmallCaps" color="text72">
          <Icon type="Culture" color={theme.colors.textLight} size={16} />
          <span>{t('general.feedback.culture')}</span>
        </CultureAxisLabel>
        <Plot>
          <HDivider style={{ top: '33.34%' }} />
          <HDivider style={{ top: '66.67%' }} />
          <VDivider style={{ left: '33.34%' }} />
          <VDivider style={{ left: '66.67%' }} />
          {view === 'heatmap' ? (
            <InsightsMatrixHeatmapView
              zoneCounts={zoneCounts}
              zoneCountsPrev={zoneCountsPrev}
              totalMembers={memberPlots.length}
              memberById={memberById}
              plotByMemberId={plotByMemberId}
              plotByMemberIdPrev={plotByMemberIdPrev}
              compareActive={!!compareTo}
              selectedTerm={selectedTerm}
              compareTo={compareTo}
              selectedZoneKey={selectedZoneKey}
              onZoneClick={(zone) => {
                const key = `${zone.zoneX}_${zone.zoneY}`
                setSelectedZoneKey((current) => (current === key ? '' : key))
              }}
              onZoneClose={() => setSelectedZoneKey('')}
              onSelectMember={setMemberForModal}
            />
          ) : (
            <InsightsMatrixScatterView
              memberPlots={memberPlots}
              memberById={memberById}
              plotByMemberId={plotByMemberId}
              plotByMemberIdPrev={plotByMemberIdPrev}
              compareActive={!!compareTo}
              selectedTerm={selectedTerm}
              compareTo={compareTo}
              hoveredMemberId={hoveredMemberId}
              pinnedMemberId={pinnedMemberId}
              selectedClusterId={selectedClusterId}
              onMemberHover={setHoveredMemberId}
              onMemberPin={(memberId) => {
                setPinnedMemberId(memberId)
                setSelectedClusterId(undefined)
                setSelectedZoneKey('')
              }}
              onClusterClick={(cluster) => {
                setSelectedClusterId((current) =>
                  current === cluster.id ? undefined : cluster.id
                )
                setPinnedMemberId(undefined)
                setSelectedZoneKey('')
              }}
              onPopoverClose={() => {
                setPinnedMemberId(undefined)
                setSelectedClusterId(undefined)
              }}
              onSelectMember={setMemberForModal}
            />
          )}
        </Plot>
      </PlotFrame>
    </Container>
  )
}

export default InsightsMatrix

const Container = styled.div`
  background-color: ${({ theme }) => theme.colors.textLightest};
  border-radius: 12px;
  padding: 8px;
  position: relative;
  display: flex;
  flex-direction: column;
`

const PlotFrame = styled.div`
  position: relative;
  height: 560px;
  padding: 8px;
`

const TopRightIcon = styled(Icon)`
  position: absolute;
  margin: 0;
  top: 8px;
  right: 8px;
`

const BottomLeftIcon = styled(Icon)`
  position: absolute;
  margin: 0;
  bottom: 12px;
  left: 12px;
`

const SkillAxisLine = styled.div`
  position: absolute;
  height: calc(100% - 72px);
  top: 20px;
  left: 20px;
  width: 2px;
  z-index: 1;
  background-color: ${({ theme }) => theme.colors.textLighter};
`

const CultureAxisLine = styled.div`
  position: absolute;
  z-index: 1;
  width: calc(100% - 72px);
  bottom: 20px;
  left: 52px;
  height: 2px;
  background-color: ${({ theme }) => theme.colors.textLighter};
`

const SkillAxisLabel = styled(Text)`
  position: absolute;
  /* Centre the rotated label on the y-axis line (left:20px, 2px wide → centre
     21px) via translateX(-50%), so it stays on the axis regardless of label
     width across languages (e.g. EN "Skills" vs longer NL "Vaardigheden"). */
  left: 21px;
  bottom: 102px;
  transform: translateX(-50%) rotate(-90deg);
  z-index: 2;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.textLightest};
  padding: 0 8px;
`

const CultureAxisLabel = styled(Text)`
  position: absolute;
  bottom: 13px;
  left: 68px;
  z-index: 2;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.textLightest};
  padding: 0 8px;
`

const Plot = styled.div`
  position: absolute;
  inset: 20px 30px 22px 22px;
`

const HDivider = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 0;
  border-top: 2px dashed ${({ theme }) => theme.colors.text72};
  opacity: 0.2;
  pointer-events: none;
`

const VDivider = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 2px dashed ${({ theme }) => theme.colors.text72};
  opacity: 0.2;
  pointer-events: none;
`
